import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

import { Features, Permissions } from '../enum/FeaturePermissions';
import { TenantService } from '../../authentication/service/tenantService';
import { selectUserPermissions, selectUserRole } from '../../authentication/state/selector';

@Component({
  selector: 'app-side-navbar',
  templateUrl: './side-navbar.component.html',
  styleUrls: ['./side-navbar.component.css'],
  imports: [Menu],
  encapsulation: ViewEncapsulation.None,
})
export class SideNavbarComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly tenantService = inject(TenantService);

  private readonly destroy$ = new Subject<void>();

  sideMenuItems: MenuItem[] | undefined;

  constructor(...args: unknown[]);
  constructor() {}

  private twoLineLabel(label: string): string {
    const parts = label.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 2) return `${parts[0]}\n${parts[1]}`;
    return label;
  }

  private toRoute(path: string): string {
    if (!path) return '/';
    return path.startsWith('/') ? path : `/${path}`;
  }

  ngOnInit() {
    const role$ = this.store.select(selectUserRole).pipe(distinctUntilChanged());
    const sectionBasedReviewEnabled$ = this.tenantService.isSectionBasedReviewEnabled().pipe(distinctUntilChanged());
    const userPermissions$ = this.store.select(selectUserPermissions).pipe(distinctUntilChanged());

    combineLatest([role$, sectionBasedReviewEnabled$, userPermissions$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userRole, sectionBasedReviewEnabled, userPermissions]) => {
        const requestsMenuItem: MenuItem = {
          label: this.twoLineLabel('Requests'),
          icon: 'fa-solid fa-id-card',
          automationId: 'request-menu-item',
          routerLink: this.toRoute('requests'),
          routerLinkActiveOptions: { exact: false },
          visible: true,
        };

        const suppliersMenuItem: MenuItem = {
          label: this.twoLineLabel('Suppliers'),
          icon: 'fa-solid fa-user',
          automationId: 'supplier-menu-item',
          routerLink: this.toRoute('suppliers'),
          routerLinkActiveOptions: { exact: false },
          visible: userRole !== 'supplier',
        };

        const formsMenuItem: MenuItem = {
          label: this.twoLineLabel('Forms'),
          icon: 'fa-solid fa-list-ol',
          routerLink: this.toRoute('forms'),
          routerLinkActiveOptions: { exact: false },
          automationId: 'forms-menu-item',
          visible: userRole !== 'supplier',
        };

        const usersMenuItem: MenuItem = {
          label: this.twoLineLabel('Users'),
          icon: 'fa-solid fa-user-pen',
          routerLink: this.toRoute('users'),
          routerLinkActiveOptions: { exact: false },
          automationId: 'users-menu-item',
          visible: userRole === 'admin' || userRole === 'supplier',
        };

        const approvalMatrixSectionBasedMenuItem: MenuItem = {
          label: this.twoLineLabel('Approval matrix'),
          icon: 'fa-solid fa-user-check',
          routerLink: this.toRoute('approval-matrix-section-based'),
          routerLinkActiveOptions: { exact: false },
          automationId: 'approval-matrix-section-based-menu-item',
          visible: userRole !== 'supplier' && sectionBasedReviewEnabled,
        };

        const approvalMatrixMenuItem: MenuItem = {
          label: this.twoLineLabel(sectionBasedReviewEnabled ? 'Approval matrix (legacy)' : 'Approval matrix'),
          icon: 'fa-solid fa-user-check',
          routerLink: this.toRoute('approval-matrix-request'),
          routerLinkActiveOptions: { exact: false },
          automationId: 'approval-matrix-menu-item',
          visible: userRole !== 'supplier',
        };

        const auditingMenuItem: MenuItem = {
          label: this.twoLineLabel('Audit log'),
          icon: 'fa-solid fa-clock-rotate-left',
          routerLink: this.toRoute('auditing'),
          routerLinkActiveOptions: { exact: false },
          automationId: 'auditing-menu-item',
          visible: userRole === 'admin',
        };

        const hasReportingAccess =
          userRole !== 'supplier' &&
          (userPermissions?.some((p) => p.feature === Features.Reporting && p.actions?.includes(Permissions.Read)) ??
            false);

        const reportingMenuItem: MenuItem = {
          label: this.twoLineLabel('Reporting'),
          icon: 'fa-solid fa-chart-column',
          routerLink: this.toRoute('reporting'),
          routerLinkActiveOptions: { exact: false },
          automationId: 'reporting-menu-item',
          visible: hasReportingAccess,
        };

        const items: MenuItem[] = [
          requestsMenuItem,
          { separator: true, visible: userRole !== 'supplier' },
          suppliersMenuItem,
          { separator: true },
          usersMenuItem,
          { separator: true, visible: userRole === 'admin' || userRole === 'supplier' },
          approvalMatrixSectionBasedMenuItem,
          approvalMatrixMenuItem,
          { separator: true, visible: userRole !== 'supplier' },
          formsMenuItem,
          { separator: true, visible: hasReportingAccess },
          reportingMenuItem,
          { separator: true, visible: userRole === 'admin' },
          auditingMenuItem,
          { separator: true, visible: userRole === 'admin' },
        ];

        this.sideMenuItems = [...items];
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
